<script setup lang="ts">
import { EDialog, formatBinding, useShortcutRegistry } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useOverlays } from '@/composables/useOverlays'

/**
 * The `?` cheat sheet (§11).
 *
 * It renders the live shortcut registry rather than a hand-maintained list, so
 * the sheet cannot drift from the bindings that actually exist: a screen that
 * registers a shortcut documents it by registering it.
 */

const overlays = useOverlays()
const { grouped } = useShortcutRegistry()
const { t, te } = useI18n()

/**
 * Shortcut descriptions are *registered as message keys* rather than as words.
 * A binding is registered once, when the screen that owns it mounts, so a
 * literal would be frozen in whichever locale was active at that moment and
 * would not follow a language switch. Anything that is not a known key is shown
 * as written, which keeps a third-party registration readable.
 */
function describe(text: string): string {
  return te(text) ? t(text) : text
}

const sections = computed(() =>
  grouped.value.map((section) => {
    const key = `shell.cheatSheet.groups.${section.group}`
    return {
      // A group the catalogue has no name for is a group some screen invented;
      // showing its raw id beats showing nothing.
      label: te(key) ? t(key) : section.group,
      shortcuts: section.shortcuts,
    }
  }),
)
</script>

<template>
  <EDialog
    v-model:open="overlays.cheatSheetOpen.value"
    :title="$t('shell.cheatSheet.title')"
    :description="$t('shell.cheatSheet.description')"
    size="md"
  >
    <div class="stack">
      <section v-for="section in sections" :key="section.label" class="stack--tight">
        <h3 class="cheat__heading">{{ section.label }}</h3>
        <dl class="cheat__list">
          <template v-for="shortcut in section.shortcuts" :key="shortcut.id">
            <dt><kbd class="kbd">{{ formatBinding(shortcut.keys) }}</kbd></dt>
            <dd>{{ describe(shortcut.description) }}</dd>
          </template>
        </dl>
      </section>

      <p v-if="sections.length === 0" class="muted text-xs">
        {{ $t('shell.cheatSheet.empty') }}
      </p>
    </div>
  </EDialog>
</template>

<style>
.cheat__heading {
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-2xs);
  font-weight: var(--erpx-weight-semibold);
  letter-spacing: var(--erpx-tracking-wide);
  text-transform: uppercase;
}

.cheat__list {
  display: grid;
  grid-template-columns: 8rem 1fr;
  gap: var(--erpx-space-1) var(--erpx-space-3);
  margin: 0;
  font-size: var(--erpx-text-xs);
}

.cheat__list dd {
  margin: 0;
}
</style>
